import {ReportBuilderComponent} from '../../components/report-builder/report-builder.component';
import { Type } from '@angular/core';
import { RoyalTicketingComponent, RtsAdminComponent } from '../../components/royal-ticketing/royal-ticketing.component';

// Import all required components
import { DashboardComponent } from '../../components/dashboard/dashboard.component';
import { RiskBoardComponent } from '../../components/risk-board/risk-board.component';
import { HedgeRollComponent } from '../../components/hedge-roll/hedge-roll.component';
import { TradeTicketsComponent } from '../../components/trade-tickets/trade-tickets.component';
import { TransactionLogComponent } from '../../components/transaction-log/transaction-log.component';
import { ReportsComponent } from '../../components/reports/reports.component';
import { InventoryTypesComponent } from '../../components/inventory-types/inventory-types.component';
import { InventoryComponent } from '../../components/CoffeeComponents/inventory/inventory-management/inventory.component';
import { ReservesComponent } from '../../components/reserves/reserves.component';
import { BeanLogicComponent } from '../../components/bean-logic/bean-logic.component';
import { SuppliersComponent } from '../../components/CoffeeComponents/inventory/suppliers/suppliers.component';
import { PurchaseOrdersComponent } from '../../components/CoffeeComponents/inventory/purchase-orders/purchase-orders.component';
import { DecafSchedulesComponent } from '../../components/decaf-schedules/decaf-schedules.component';
import { CuppingComponent } from '../../components/cupping/cupping.component';
import { CustomerSamplesComponent } from '../../components/customer-samples/customer-samples.component';
import { InventoryTrackerComponent } from '../../components/inventory-tracker/inventory-tracker.component';
import { SteamshipRatesComponent } from '../../components/steamship-rates/steamship-rates.component';
import { SteamshipTrackerComponent } from '../../components/steamship-tracker/steamship-tracker.component';
import { OrderQueueComponent } from '../../components/order-queue/order-queue.component';
import { FreightProvidersComponent } from '../../components/freight-providers/freight-providers.component';
import { OrganicInventoryComponent } from '../../components/organic-inventory/organic-inventory.component';
import { PickListComponent } from '../../components/pick-list/pick-list.component';
import { TruckerScheduleComponent } from '../../components/trucker-schedule/trucker-schedule.component';
import { DrayageScheduleComponent } from '../../components/drayage-schedule/drayage-schedule.component';
import { InboundArrivalsComponent } from '../../components/inbound-arrivals/inbound-arrivals.component';
import { LedgerComponent } from '@coffee/accounting/ledger/ledger.component';
import { CustomerPaymentsComponent } from '../../components/customer-payments/customer-payments.component';
import { AuditComponent } from '../../components/audit/audit.component';
import { ErrorLogComponent } from '../../components/error-log/error-log.component';
import { SystemSettingsComponent } from '../../components/system-settings/system-settings.component';
import { LocationManagementComponent } from '../../components/CoffeeComponents/inventory/location-management/location-management.component';
import { SslRateComponent } from '../../components/CoffeeComponents/inventory/ssl-rate/ssl-rate.component';
import { FileExplorerComponent } from '../../components/file-explorer/file-explorer.component'
import { CoffeesComponent } from '@coffee/inventory/coffees/coffees.component';
import { CoffeeCuppingSessionComponent } from '@coffee/quality-control/coffee-cupping-session/coffee-cupping-session.component';
import { OfferSamplesComponent } from '@coffee/quality-control/offer-samples/offer-samples.component';
import { SkuPricingPrepComponent } from '@coffee/inventory/sku-pricing-prep/sku-pricing-prep.component';
import { OrderManagementComponent } from '../../components/CoffeeComponents/CRM/order-management/order-management.component';
import { InventoryTransferComponent } from '@coffee/inventory/inventory-transfer/inventory-transfer.component';
import { CustomerManagementComponent } from '@coffee/CRM/customer-management/customer-management.component';
import { OutboundManagementComponent } from '../../components/CoffeeComponents/CRM/outbound-management/outbound-management.component';
import { SwapManagementComponent } from '../../components/CoffeeComponents/CRM/swap-management/swap-management.component';
import { EarlyWarningComponent } from '@coffee/inventory/early-warning/early-warning.component';
import { GroupAnalysisPageComponent } from '@coffee/inventory/early-warning/group-analysis-page/group-analysis-page.component';
import { EwsHistoryComponent } from '@coffee/inventory/early-warning/ews-history/ews-history.component';
import { EwsBeanLogicComponent } from '@coffee/inventory/early-warning/bean-logic/bean-logic.component';
import { EwsReportsComponent } from '@coffee/inventory/early-warning/ews-reports/ews-reports.component';
import { EwsPriceLogicComponent } from '@coffee/inventory/early-warning/price-logic/price-logic.component';
import { BeanAccessComponent } from '@coffee/inventory/early-warning/bean-access/bean-access.component';
import { FuturesManagementComponent } from '../../components/CoffeeComponents/CRM/futures-management/futures-management.component';

// Define the interface for navigation items
export interface NavItem {
  title: string;
  icon: string;
  svgIcon?: boolean;
  componentKey: string;
  component?: Type<any>;
  children?: NavItem[];
  parent?: string;
}

// Define the navigation structure
export const NAVIGATION_DATA: NavItem[] = [

  {
    title: 'Inventory',
    icon: 'inventory2',
    children: [
      { title: 'Supplier/Vender Management', icon: 'sailing', component: SuppliersComponent, componentKey: 'suppliers' },
      { title: 'Coffee SKU', icon: 'coffee_bean', svgIcon: true, component: CoffeesComponent, componentKey: 'coffees' },
      { title: 'Purchase Orders', icon: 'shopping_basket', component: PurchaseOrdersComponent, componentKey: 'purchaseOrders' },
      { title: 'Inventory Transfer', icon: 'swap_horiz', component: InventoryTransferComponent, componentKey: 'inventoryTransfer' },
      { title: 'Inventory Management', icon: 'inventory', component: InventoryComponent, componentKey: 'inventory' },
      { title: 'SSL Rates', icon: 'directions_boat', component: SslRateComponent, componentKey: 'sslRates' },
      { title: 'SKU Pricing', icon: 'price_check', component: SkuPricingPrepComponent, componentKey: 'skuPricingPrep' },
      { title: 'Futures Management', icon: 'trending_up', component: FuturesManagementComponent, componentKey: 'futuresManagement' }
    ],
    componentKey: 'inv-management'
  },
  {
    // Scope 6.4 — own heading like old RMS ("EARLY WARNING SYSTEM"); child keys unchanged so open tabs / favorites keep working
    title: 'Early Warning System',
    icon: 'warning_amber',
    children: [
      { title: 'EWS Summary', icon: 'warning_amber', component: EarlyWarningComponent, componentKey: 'earlyWarning' },
      { title: 'EWS Group Analysis', icon: 'insights', component: GroupAnalysisPageComponent, componentKey: 'ewsGroupAnalysis' },
      { title: 'EWS Saved Forecasts', icon: 'history', component: EwsHistoryComponent, componentKey: 'ewsHistory' },
      { title: 'Bean Logic', icon: 'coffee', component: EwsBeanLogicComponent, componentKey: 'ewsBeanLogic' },
      { title: 'EWS Reports & Setup', icon: 'assessment', component: EwsReportsComponent, componentKey: 'ewsReports' },
      { title: 'Price Logic', icon: 'price_check', component: EwsPriceLogicComponent, componentKey: 'ewsPriceLogic' },
      { title: 'Bean Logic Access', icon: 'security', component: BeanAccessComponent, componentKey: 'ewsBeanAccess' },
    ],
    componentKey: 'earlyWarningSystem'
  },
  {
    title: 'Quality Control',
    icon: 'grading',
    children: [
      { title: 'Cupping Session', icon: 'coffee', component: CoffeeCuppingSessionComponent, componentKey: 'coffeeCuppingSession' },
      { title: 'Cupping Samples', icon: 'grading', component: OfferSamplesComponent, componentKey: 'cuppingSamples' }
    ],
    componentKey: 'qualityControl'
  },
  {
    title: 'Customer Management',
    icon: 'contacts',
    children: [
      { title: 'Customer Management', icon: 'contacts', component: CustomerManagementComponent, componentKey: 'customerManagement' },
      { title: 'Order Queue', icon: 'point_of_sale', component: OrderManagementComponent, componentKey: 'orderManagement' },
      { title: 'Reserves', icon: 'lock_reset', component: ReservesComponent, componentKey: 'reserves' },
      { title: 'Swap Management', icon: 'swap_horiz', component: SwapManagementComponent, componentKey: 'swapManagement' },
    ],
    componentKey: 'customerMgmt'
  },
  {
    title: 'Accounting',
    icon: 'attach_money',
    children: [
      { title: 'Ledger', icon: 'balance', component: LedgerComponent, componentKey: 'ledger' },
      { title: 'Customer Payments', icon: 'attach_money', component: CustomerPaymentsComponent, componentKey: 'customerPayments' },
    ],
    componentKey: 'accounting'
  },
  {
    title: 'Royal Ticketing', icon: 'confirmation_number', componentKey: 'royalTicketing', children: [
      { title: 'My Tickets', icon: 'confirmation_number', component: RoyalTicketingComponent, componentKey: 'rtsTickets' },
      { title: 'IT Admin', icon: 'support_agent', component: RtsAdminComponent, componentKey: 'rtsAdmin' }
    ]
  },
  {
    title: 'Risk Management · Concepts', icon: 'area_chart', componentKey: 'riskConcepts',
    children: [
      { title: 'Risk Board · Concept', icon: 'insert_chart', component: RiskBoardComponent, componentKey: 'riskBoardConcept' },
      { title: 'Trade Tickets · Concept', icon: 'sticky_note_2', component: TradeTicketsComponent, componentKey: 'tradeTicketsConcept' },
      { title: 'Hedge Roll · Concept', icon: 'drive_file_move', component: HedgeRollComponent, componentKey: 'hedgeRollConcept' },
      { title: 'Impact Log · Concept', icon: 'history', component: TransactionLogComponent, componentKey: 'impactLogConcept' }
    ]
  },
  {
    title: 'Utilities',
    icon: 'build_circle',
    children: [
      {title:'Report Builder',icon:'assessment',component:ReportBuilderComponent,componentKey:'reportBuilder'},
      { title: 'File Explorer', icon: 'folder_open', component: FileExplorerComponent, componentKey: 'fileExplorer' }
    ],
    componentKey: 'util'
  },

  //{ title: 'Dashboard', icon: 'dashboard', component: DashboardComponent },

  //{
  //  title: 'Risk Management',
  //  icon: 'area_chart',
  //  children: [
  //    { title: 'Risk Board', icon: 'insert_chart', component: RiskBoardComponent },
  //    { title: 'Hedge Roll', icon: 'drive_file_move', component: HedgeRollComponent },
  //    { title: 'Trade Tickets', icon: 'sticky_note', component: TradeTicketsComponent },
  //    { title: 'Transaction Log', icon: 'low_priority', component: TransactionLogComponent },
  //    { title: 'Reports', icon: 'file_copy', component: ReportsComponent },
  //  ]
  //},

  //{
  //  title: 'Sales',
  //  icon: 'moving',
  //  children: [
  //    { title: 'Inventory Types', icon: 'type_specimen', component: InventoryTypesComponent },
  //    { title: 'Inventory', icon: 'inventory', component: InventoryComponent },
  //    { title: 'Customers', icon: 'people_alt', component: CustomersComponent },
  //    { title: 'Sales Orders', icon: 'point_of_sale', component: SalesOrdersComponent },
  //    { title: 'Reserves', icon: 'lock_reset', component: ReservesComponent },
  //    { title: 'Bean Logic', icon: 'calculate', component: BeanLogicComponent },
  //    { title: 'Reports', icon: 'file_copy', component: ReportsComponent },
  //  ]
  //},

  //{
  //  title: 'Purchasing',
  //  icon: 'format_indent_increase',
  //  children: [
  //    { title: 'Suppliers', icon: 'maps_home_work', component: SuppliersComponent },
  //    { title: 'Purchase Orders', icon: 'add_business', component: PurchaseOrdersComponent },
  //    { title: 'Decaf Schedules', icon: 'schedule_send', component: DecafSchedulesComponent },
  //    { title: 'Reports', icon: 'file_copy', component: ReportsComponent },
  //  ]
  //},

  //{
  //  title: 'Quality Control',
  //  icon: 'grading',
  //  children: [
  //    { title: 'Cupping', icon: 'coffee', component: CuppingComponent },
  //    { title: 'Customer Samples', icon: 'coffee_maker', component: CustomerSamplesComponent },
  //    { title: 'Reports', icon: 'file_copy', component: ReportsComponent },
  //  ]
  //},

  //{
  //  title: 'Inbound',
  //  icon: 'input',
  //  children: [
  //    { title: 'Inventory Tracker', icon: 'checklist', component: InventoryTrackerComponent },
  //    { title: 'Steamship Rates', icon: 'anchor', component: SteamshipRatesComponent },
  //    { title: 'Steamship Tracker', icon: 'directions_boat', component: SteamshipTrackerComponent },
  //    { title: 'Purchase Orders', icon: 'add_business', component: PurchaseOrdersComponent },
  //    { title: 'Reports', icon: 'file_copy', component: ReportsComponent },
  //  ]
  //},

  //{
  //  title: 'Outbound',
  //  icon: 'outbound',
  //  children: [
  //    { title: 'Order Queue', icon: 'queue', component: OrderQueueComponent },
  //    { title: 'Freight Providers', icon: 'local_shipping', component: FreightProvidersComponent },
  //    { title: 'Organic Inventory', icon: 'energy_savings_leaf', component: OrganicInventoryComponent },
  //    { title: 'Reports', icon: 'file_copy', component: ReportsComponent },
  //  ]
  //},

  //{
  //  title: 'Warehouse',
  //  icon: 'warehouse',
  //  children: [
  //    { title: 'Pick List', icon: 'list_alt', component: PickListComponent },
  //    { title: 'Trucker Schedule', icon: 'local_shipping', component: TruckerScheduleComponent },
  //    { title: 'Drayage Schedule', icon: 'schedule', component: DrayageScheduleComponent },
  //    { title: 'Inbound Arrivals', icon: 'move_to_inbox', component: InboundArrivalsComponent },
  //    { title: 'Reports', icon: 'file_copy', component: ReportsComponent },
  //  ]
  //},

  //{
  //  title: 'Accounting',
  //  icon: 'attach_money',
  //  children: [
  //    { title: 'Ledger', icon: 'balance', component: LedgerComponent },
  //    { title: 'Customer Payments', icon: 'attach_money', component: CustomerPaymentsComponent },
  //    { title: 'Audit', icon: 'assured_workload', component: AuditComponent },
  //    { title: 'Reports', icon: 'file_copy', component: ReportsComponent },
  //  ]
  //},

  //{
  //  title: 'Administration',
  //  icon: 'admin_panel_settings',
  //  children: [
  //    { title: 'Error Log', icon: 'error_outline', component: ErrorLogComponent },
  //    { title: 'System Settings', icon: 'settings', component: SystemSettingsComponent },
  //  ]
  //}
];
